// @ts-nocheck
import React, { useState, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Plus, Pencil, Trash2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { useAdminOrgScope } from "@/hooks/useAdminOrgScope";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface DiscountPolicy {
  id: string;
  name: string;
  description?: string | null;
  discount_type?: string | null;
  discount_value?: number | null;
  applies_to?: string | null;
  condition_type?: string | null;
  condition_value?: number | null;
  fixed_scope?: string | null;
  is_active: boolean;
  created_at: string;
}

interface PolicyFormData {
  name: string;
  description: string;
  discount_type: string;
  discount_value: number | string;
  applies_to: string;
  condition_type: string;
  condition_value: number | string;
  fixed_scope: string;
  /** UI-only: drives the worked example, never saved. */
  example_entry_fee: number | string;
  is_active: boolean;
}

const emptyForm: PolicyFormData = {
  name: "",
  description: "",
  discount_type: "",
  discount_value: "",
  applies_to: "",
  condition_type: "",
  condition_value: "",
  fixed_scope: "per_order",
  example_entry_fee: 375,
  is_active: true,
};

const EventDiscountPoliciesManagement = () => {
  const { scopeOrgId, createOrgId, ready } = useAdminOrgScope();
  const [policies, setPolicies] = useState<DiscountPolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState<PolicyFormData>(emptyForm);

  const [editingPolicy, setEditingPolicy] = useState<DiscountPolicy | null>(null);
  const [editForm, setEditForm] = useState<PolicyFormData>(emptyForm);
  const [savingEdit, setSavingEdit] = useState(false);

  const [deletingPolicy, setDeletingPolicy] = useState<DiscountPolicy | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!ready) return;
    fetchDiscountPolicies();
  }, [ready, scopeOrgId]);

  const fetchDiscountPolicies = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from("event_discount_policies")
        .select(
          "id, name, description, discount_type, discount_value, applies_to, condition_type, condition_value, fixed_scope, is_active, created_at"
        )
        .order("name", { ascending: true });

      // Org admin -> only their policies; super admin -> all policies.
      if (scopeOrgId) query = query.eq("organization_id", scopeOrgId);

      const { data, error } = await query;

      if (error) throw error;
      setPolicies((data as DiscountPolicy[]) || []);
    } catch (error) {
      console.error("Error fetching discount policies:", error);
      toast.error("Failed to load discount policies");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof PolicyFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditChange = (field: keyof PolicyFormData, value: any) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const openEdit = (policy: DiscountPolicy) => {
    setEditingPolicy(policy);
    setEditForm({
      name: policy.name ?? "",
      description: policy.description ?? "",
      discount_type: policy.discount_type ?? "",
      discount_value: policy.discount_value ?? "",
      applies_to: policy.applies_to ?? "",
      condition_type: policy.condition_type ?? "",
      condition_value: policy.condition_value ?? "",
      fixed_scope: policy.fixed_scope ?? "per_order",
      example_entry_fee: 375,
      is_active: policy.is_active,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !formData.name ||
      !formData.discount_type ||
      formData.discount_value === "" ||
      !formData.applies_to ||
      !formData.condition_type
    ) {
      toast.error("Please fill in all required fields");
      return;
    }
    try {
      setCreating(true);
      const { error } = await supabase.from("event_discount_policies").insert({
        name: formData.name,
        description: formData.description || null,
        discount_type: formData.discount_type,
        discount_value: Number(formData.discount_value),
        applies_to: formData.applies_to,
        condition_type: formData.condition_type,
        condition_value:
          formData.condition_value !== "" ? Number(formData.condition_value) : null,
        fixed_scope:
          formData.discount_type === "percentage"
            ? "per_order"
            : formData.fixed_scope || "per_order",
        is_active: formData.is_active,
        organization_id: createOrgId,
      });
      if (error) throw error;
      toast.success("Discount policy created successfully");
      setFormData(emptyForm);
      await fetchDiscountPolicies();
    } catch (error) {
      console.error("Error creating discount policy:", error);
      toast.error("Failed to create discount policy");
    } finally {
      setCreating(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingPolicy) return;
    if (
      !editForm.name ||
      !editForm.discount_type ||
      editForm.discount_value === "" ||
      !editForm.applies_to ||
      !editForm.condition_type
    ) {
      toast.error("Please fill in all required fields");
      return;
    }
    try {
      setSavingEdit(true);
      const { error } = await supabase
        .from("event_discount_policies")
        .update({
          name: editForm.name,
          description: editForm.description || null,
          discount_type: editForm.discount_type,
          discount_value: Number(editForm.discount_value),
          applies_to: editForm.applies_to,
          condition_type: editForm.condition_type,
          condition_value:
            editForm.condition_value !== "" ? Number(editForm.condition_value) : null,
          fixed_scope:
            editForm.discount_type === "percentage"
              ? "per_order"
              : editForm.fixed_scope || "per_order",
          is_active: editForm.is_active,
        })
        .eq("id", editingPolicy.id);
      if (error) throw error;
      toast.success("Discount policy updated");
      setEditingPolicy(null);
      await fetchDiscountPolicies();
    } catch (error) {
      console.error("Error updating discount policy:", error);
      toast.error("Failed to update discount policy");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingPolicy) return;
    try {
      setDeleting(true);
      const { error } = await supabase
        .from("event_discount_policies")
        .delete()
        .eq("id", deletingPolicy.id);
      if (error) throw error;
      toast.success("Discount policy deleted");
      setDeletingPolicy(null);
      await fetchDiscountPolicies();
    } catch (error) {
      console.error("Error deleting discount policy:", error);
      toast.error("Failed to delete discount policy");
    } finally {
      setDeleting(false);
    }
  };

  const renderPolicyFields = (
    data: PolicyFormData,
    onChange: (field: keyof PolicyFormData, value: any) => void,
    idPrefix: string
  ) => {
    const isPercent = data.discount_type === "percentage";
    const isFixed = data.discount_type === "fixed";
    const perTeam = isFixed && data.fixed_scope === "per_team";
    const exampleTeams = Math.max(2, Number(data.condition_value) || 2);
    const exampleEntry = Number(data.example_entry_fee) || 375;
    const value = Number(data.discount_value) || 0;
    const entryTotal = exampleTeams * exampleEntry;
    const exampleOff = isPercent
      ? Math.min(entryTotal * (value / 100), entryTotal)
      : perTeam
      ? Math.min(value * exampleTeams, entryTotal)
      : Math.min(value, entryTotal);
    const money = (n: number) =>
      `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor={`${idPrefix}-name`}>Name *</Label>
            <Input
              id={`${idPrefix}-name`}
              value={data.name}
              onChange={(e) => onChange("name", e.target.value)}
              placeholder="Enter policy name"
              required
            />
          </div>
          <div>
            <Label htmlFor={`${idPrefix}-discount_type`}>Discount Type *</Label>
            <Select
              value={data.discount_type}
              onValueChange={(v) => onChange("discount_type", v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select discount type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="percentage">Percentage (%) off entry fees</SelectItem>
                <SelectItem value="fixed">Fixed dollar amount ($) off entry fees</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label htmlFor={`${idPrefix}-description`}>Description</Label>
          <Textarea
            id={`${idPrefix}-description`}
            value={data.description}
            onChange={(e) => onChange("description", e.target.value)}
            placeholder="Enter policy description"
            rows={3}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor={`${idPrefix}-discount_value`}>
              {isPercent
                ? "Percentage off (%) *"
                : isFixed
                ? "Dollar amount off ($) *"
                : "Discount Value *"}
            </Label>
            <div className="relative">
              {isFixed && (
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  $
                </span>
              )}
              <Input
                id={`${idPrefix}-discount_value`}
                type="number"
                className={isFixed ? "pl-7" : isPercent ? "pr-8" : undefined}
                value={data.discount_value}
                onChange={(e) => onChange("discount_value", e.target.value)}
                placeholder={isPercent ? "e.g. 25" : "e.g. 50.00"}
                min="0"
                step="0.01"
                required
              />
              {isPercent && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  %
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {isPercent
                ? "A percent of the combined entry fees of the qualifying teams."
                : isFixed
                ? "A dollar amount — choose below whether it applies per team or per order."
                : "Pick a discount type first."}
            </p>
          </div>
          <div>
            <Label htmlFor={`${idPrefix}-applies_to`}>Applies To *</Label>
            <Select
              value={data.applies_to}
              onValueChange={(v) => onChange("applies_to", v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select application" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="team">Team</SelectItem>
                <SelectItem value="individual">Individual</SelectItem>
                <SelectItem value="bundle">Bundle</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor={`${idPrefix}-condition_type`}>Condition Type *</Label>
            <Select
              value={data.condition_type}
              onValueChange={(v) => onChange("condition_type", v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select condition" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="min_teams">Minimum Teams</SelectItem>
                <SelectItem value="min_people">Minimum People</SelectItem>
                <SelectItem value="bundle_quantity">Bundle Quantity</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {isFixed && (
          <div>
            <Label htmlFor={`${idPrefix}-fixed_scope`}>Fixed amount applies *</Label>
            <Select
              value={data.fixed_scope}
              onValueChange={(v) => onChange("fixed_scope", v)}
            >
              <SelectTrigger id={`${idPrefix}-fixed_scope`}>
                <SelectValue placeholder="Select scope" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="per_order">
                  Per order — taken off once for the whole registration
                </SelectItem>
                <SelectItem value="per_team">
                  Per team — multiplied by the number of qualifying teams
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor={`${idPrefix}-condition_value`}>
              Condition Value{" "}
              {data.condition_type === "min_teams" ? "(teams required)" : ""}
            </Label>
            <Input
              id={`${idPrefix}-condition_value`}
              type="number"
              value={data.condition_value}
              onChange={(e) => onChange("condition_value", e.target.value)}
              placeholder="Enter condition value"
              min="0"
            />
          </div>
          <div className="flex items-center space-x-2 pt-6">
            <Checkbox
              id={`${idPrefix}-is_active`}
              checked={data.is_active}
              onCheckedChange={(checked) => onChange("is_active", checked)}
            />
            <Label htmlFor={`${idPrefix}-is_active`}>Active</Label>
          </div>
        </div>

        {/* Worked example — updates live as the organiser types */}
        <div className="rounded-md border bg-muted/40 p-4 space-y-2">
          <div className="flex flex-wrap items-end gap-3">
            <div className="text-sm font-medium">Worked example</div>
            <div className="flex items-center gap-2">
              <Label htmlFor={`${idPrefix}-example`} className="text-xs font-normal">
                Entry fee per team
              </Label>
              <Input
                id={`${idPrefix}-example`}
                type="number"
                className="h-8 w-28"
                value={data.example_entry_fee}
                onChange={(e) => onChange("example_entry_fee", e.target.value)}
                min="0"
                step="1"
              />
            </div>
          </div>
          {!data.discount_type || value <= 0 ? (
            <p className="text-sm text-muted-foreground">
              Choose a discount type and enter a value to see what a coach would pay.
            </p>
          ) : (
            <>
              <p className="text-sm">
                {exampleTeams} teams at {money(exampleEntry)} = {money(entryTotal)} in entry
                fees →{" "}
                <span className="font-semibold">{money(exampleOff)} off</span> (coach pays{" "}
                {money(entryTotal - exampleOff)})
              </p>
              <p className="text-xs text-muted-foreground">
                {isPercent
                  ? `${value}% of the combined entry fees.`
                  : perTeam
                  ? `${money(value)} × ${exampleTeams} teams.`
                  : `${money(value)} once for the whole order, no matter how many teams.`}{" "}
                Discounts never apply to scheduling requests, tier fees, or extras.
              </p>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Create New Discount Policy
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {renderPolicyFields(formData, handleInputChange, "create")}
            <div className="flex justify-end">
              <Button type="submit" disabled={creating}>
                {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create Policy
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Event Discount Policies</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              <span>Loading discount policies...</span>
            </div>
          ) : policies.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center">
              No discount policies found.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Discount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {policies.map((policy) => (
                  <TableRow key={policy.id}>
                    <TableCell className="font-medium">{policy.name}</TableCell>
                    <TableCell>
                      {policy.discount_value == null
                        ? "N/A"
                        : policy.discount_type === "percentage" ||
                          policy.discount_type === "percent"
                        ? `${policy.discount_value}% off entry fees`
                        : `$${Number(policy.discount_value).toFixed(2)} off entry fees ${
                            (policy.fixed_scope ?? "per_order") === "per_team"
                              ? "per team"
                              : "per order"
                          }`}
                    </TableCell>
                    <TableCell>
                      <Badge variant={policy.is_active ? "default" : "secondary"}>
                        {policy.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(policy.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEdit(policy)}
                        >
                          <Pencil className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => setDeletingPolicy(policy)}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog
        open={!!editingPolicy}
        onOpenChange={(open) => !open && setEditingPolicy(null)}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Discount Policy</DialogTitle>
          </DialogHeader>
          {renderPolicyFields(editForm, handleEditChange, "edit")}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditingPolicy(null)}
              disabled={savingEdit}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={savingEdit}>
              {savingEdit && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <AlertDialog
        open={!!deletingPolicy}
        onOpenChange={(open) => !open && setDeletingPolicy(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete discount policy?</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingPolicy
                ? `"${deletingPolicy.name}" will be permanently deleted. This cannot be undone.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting}>
              {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EventDiscountPoliciesManagement;
